import { io, type Socket } from 'socket.io-client'
import type {
  Action,
  ChatPayload,
  ErrorPayload,
  JoinedPayload,
  PlayerView,
  RevealPayload,
  RoomInfoPayload,
} from './protocol-types'

/**
 * 席位令牌的存放处。
 *
 * 为什么必须持久化：主力设备是手机，而手机上「切后台 → 系统回收页面 →
 * 回来重新加载」是家常便饭。没有令牌，回来就是个新玩家、席位和目标卡都没了；
 * 有令牌，回来还是原来那个座位、还是原来那张牌。
 *
 * 按房间码分别存：三个人可能先后进过不同房间，把令牌混在一个键里会串号。
 */
const TOKEN_PREFIX = 'anqi.seatToken.'

export function loadToken(code: string): string | undefined {
  try {
    return localStorage.getItem(TOKEN_PREFIX + code.toUpperCase()) ?? undefined
  } catch {
    // 隐私模式下 localStorage 可能直接抛异常。拿不到令牌只是不能重连，
    // 不该让整个界面崩掉。
    return undefined
  }
}

export function saveToken(code: string, token: string): void {
  try {
    localStorage.setItem(TOKEN_PREFIX + code.toUpperCase(), token)
  } catch {
    /* 同上：存不下就算了，本局仍可正常玩，只是刷新后回不到原座 */
  }
}

/** 最近一次成功加入的房间码，用于「刷新后自动重连」。 */
const LAST_ROOM_KEY = 'anqi.lastRoom'

export function loadLastRoom(): string | undefined {
  try {
    return localStorage.getItem(LAST_ROOM_KEY) ?? undefined
  } catch {
    return undefined
  }
}

export function saveLastRoom(code: string): void {
  try {
    localStorage.setItem(LAST_ROOM_KEY, code.toUpperCase())
  } catch {
    /* 同上 */
  }
}

/** ack 要么是载荷，要么是 { code, message }。这个守卫把两者分开。 */
export function isError(v: unknown): v is ErrorPayload {
  return typeof v === 'object' && v !== null && 'code' in v && 'message' in v
}

export interface ServerHandlers {
  onView(v: PlayerView): void
  onRoomInfo(p: RoomInfoPayload): void
  onChat(p: ChatPayload): void
  onReveal(r: RevealPayload): void
  /**
   * 连接状态变化。**必须让玩家看见** —— 三个人不能通话，
   * 卡住的时候他们无法互相询问「是游戏挂了还是我信号断了」，
   * 界面是唯一能回答这个问题的地方。
   */
  onConnection(state: 'connecting' | 'online' | 'offline'): void
}

export interface Client {
  join(req: { code?: string; displayName: string; token?: string }): Promise<JoinedPayload>
  start(): Promise<void>
  act(action: Action): Promise<void>
  chat(text: string, preset: boolean): Promise<void>
  close(): void
}

/** 把回调式 ack 包成 Promise，并把错误载荷转成 reject。 */
function emitAsync<T>(socket: Socket, event: string, ...args: unknown[]): Promise<T> {
  return new Promise((resolve, reject) => {
    socket.emit(event, ...args, (res: unknown) => {
      if (isError(res)) reject(new ClientError(res.code, res.message))
      else resolve(res as T)
    })
  })
}

/**
 * 服务端已经把错误分好类了：要么是「你这一手不合法」这种给玩家看的解释，
 * 要么是一句固定的通用文案。两种都要**原样显示**，绝不静默吞掉 ——
 * 对一个无法开口问别人的玩家来说，点了没反应又不知道为什么是最坏的结果。
 */
export class ClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ClientError'
  }
}

export function createClient(handlers: ServerHandlers): Client {
  // 同源部署：Nginx 同时管静态文件和 socket.io 反代，所以不写 URL。
  const socket: Socket = io({ autoConnect: true })

  handlers.onConnection('connecting')
  socket.on('connect', () => handlers.onConnection('online'))
  socket.on('disconnect', () => handlers.onConnection('offline'))
  socket.io.on('reconnect_attempt', () => handlers.onConnection('connecting'))

  socket.on('view', handlers.onView)
  socket.on('roomInfo', handlers.onRoomInfo)
  socket.on('chat', handlers.onChat)
  socket.on('reveal', handlers.onReveal)

  return {
    async join(req) {
      const joined = await emitAsync<JoinedPayload>(socket, 'join', req)
      saveToken(joined.code, joined.token)
      saveLastRoom(joined.code)
      return joined
    },
    start: () => emitAsync<{ ok: true }>(socket, 'start').then(() => undefined),
    act: (action) => emitAsync<{ ok: true }>(socket, 'act', action).then(() => undefined),
    chat: (text, preset) =>
      emitAsync<{ ok: true }>(socket, 'chat', { text, preset }).then(() => undefined),
    close: () => socket.close(),
  }
}
